import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reputationItems, faqItems } from '@/mocks/faq';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('loading');
    const payload = new URLSearchParams();
    payload.set('name', (formData.get('name') as string || ''));
    payload.set('email', (formData.get('email') as string || ''));
    payload.set('subject', (formData.get('subject') as string || ''));
    payload.set('message', (formData.get('message') as string || ''));

    try {
      const res = await fetch('https://readdy.ai/api/form/d9srd2d8o1vn02ekul1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_e) { /* ignore parse error */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (String(serverMsg).includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Brand Introduction */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-16">
            <Breadcrumb items={[{ label: '关于我们' }]} />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              关于我们
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
              人渣游戏深度解析是一个专注于争议性游戏内容分析与讨论的独立平台。我们致力于为玩家提供客观、深入、全面的游戏解读，帮助每位玩家在纷繁复杂的游戏世界中做出明智的选择。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-eye-line text-xl" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">深度洞察</h3>
                <p className="text-sm text-foreground-600">透过表象看本质，挖掘游戏背后的设计理念和社会意义</p>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-scales-3-line text-xl" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">客观公正</h3>
                <p className="text-sm text-foreground-600">呈现多元观点，不回避争议，让读者自行判断</p>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-group-line text-xl" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">玩家为先</h3>
                <p className="text-sm text-foreground-600">从玩家视角出发，提供真正有价值的内容和讨论空间</p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                媒体与玩家口碑
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                来自各大游戏媒体和社区的真实评价
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reputationItems.map((item, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 font-heading font-bold text-sm">
                      {item.logo}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-heading text-base font-semibold text-foreground-900">{item.name}</h3>
                        <span className="text-sm font-label font-bold text-accent-600">{item.rating}/10</span>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.comment}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-16 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                联系我们
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                有任何问题、建议或合作意向？请通过下方表单与我们联系
              </p>
            </div>
            <form
              onSubmit={handleContactSubmit}
              data-readdy-form=""
              className="space-y-5"
            >
              <div className="form-honeypot">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden={true} readOnly />
              </div>
              <div>
                <label htmlFor="name" className="block text-sm font-label text-foreground-700 mb-1.5">姓名</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  placeholder="请输入你的姓名"
                  className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-label text-foreground-700 mb-1.5">邮箱</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  placeholder="请输入你的邮箱地址"
                  className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-label text-foreground-700 mb-1.5">主题</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  placeholder="请输入主题"
                  className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-label text-foreground-700 mb-1.5">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  placeholder="请输入你的留言内容（不超过500字）"
                  className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-label text-sm transition-all duration-200 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'loading' ? '提交中...' : '发送留言'}
              </button>
              {formStatus !== 'idle' && (
                <p className={`text-center text-sm ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                  {formMessage}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': '关于我们 - 人渣游戏深度解析',
        'description': '人渣游戏深度解析是一个专注于争议性游戏内容分析与讨论的独立平台。',
        'url': 'https://scum-game-analysis.com/about',
        'inLanguage': 'zh-CN',
        'breadcrumb': {
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://scum-game-analysis.com/' },
            { '@type': 'ListItem', 'position': 2, 'name': '关于我们' },
          ],
        },
      }) }} />
    </>
  );
}